import { Component, inject, OnInit } from '@angular/core';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { BUTTON_NAMES, LAYOUT_TYPE } from '../../model/common.model';
import { FieldSelectedValue, SECTION_TYPE, TabConfig } from '../../model/form.model';
import { DealerOverviewFilterTrigger, DealerPerformanceDashboardFilterTrigger, DealerScorecardDashboardTitle, DealerScorecardDashboardTabConfig, DealerScorecardSectionNames, SalesOverviewFilterSchema, SalesOverviewFilterTrigger } from '../../model/dealer-scorecard-dashboard.model';
import { API_STATUS, ApiEnvNames, FETCH_METHODS } from '../../model/api.model';
import { CommonService } from '../../services/common.service';
import { CommonUtils } from '../../utils/common.utils';
import { GoogleAnalyticsDealerOptionsSearchConfig } from '../../model/google-analytics.model';
import { fetchParamsEventListForDashboard } from '../../model/event-registration.model';
import { FieldNames } from '../../model/form-fields.model';
import { ReplaySubject, Subject } from 'rxjs';

@Component({
  selector: 'app-dealer-scorecard-dashboard',
  standalone: true,
  imports: [PageContainerComponent],
  templateUrl: './dealer-scorecard-dashboard.component.html',
  styleUrl: './dealer-scorecard-dashboard.component.scss',
})
export class DealerScorecardDashboardComponent implements OnInit {
  title: string = DealerScorecardDashboardTitle;
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  tabConfig: Array<TabConfig> = DealerScorecardDashboardTabConfig;
  private readonly service = inject(CommonService);
  private readonly commonUtils = inject(CommonUtils);
  private readonly dealerPerformanceDashboardPayload = new ReplaySubject<any>(1);
  private readonly dealerOverviewPayload = new ReplaySubject<any>(1);
  private readonly salesOverviewPayload = new ReplaySubject<any>(1);
  readonly westernArea ="Western Area";
  readonly centralArea ="Central Area";
  readonly easternArea ="Eastern Area";
  readonly southernArea ="Southern Area";
  ngOnInit(): void {
    this.initializeSalesOverviewFilter();
    this.bindDealerPerformanceDashboardPayload();
    this.setupScorecardFilterActions();
    this.emitDealerPerformanceDashboardPayload();
    void this.loadDealerDropdownOptions();
    this.loadRegionDropdownOptions();
    void this.loadSalesOverviewEventOptions();
  }

  private initializeSalesOverviewFilter(): void {
    this.tabConfig.forEach((tab) => {
      if (tab.type !== SECTION_TYPE.Dashboard) {
        return;
      }

      tab.tabOnChangeSubject ??= new Subject();
      tab.tabOnChangeSubject.subscribe(() => setTimeout(() => this.emitAreaYearPayloadForTab(tab)));
      this.bindAreaYearTab(tab);
    });
  }

  private bindAreaYearTab(tab: TabConfig): void {
    const gridSchema = tab.inputs?.['gridSchema'] as any[] | undefined;
    const globalFilterForm = gridSchema?.find((gridElm: any) => gridElm?.type === SECTION_TYPE.GlobalFilter)?.inputs?.dynamicFormArray;
    const formName = globalFilterForm?.formName;
    const isDealerOverview = formName === 'dealerScorecardGlobalFilter';
    const isSalesOverview = formName === 'salesOverviewGlobalFilter';
    if (!isDealerOverview && !isSalesOverview) {
      return;
    }

    globalFilterForm.onButtonClicked = () => {};
    globalFilterForm.onControlValueChanged = isDealerOverview ? this.dealerOverviewFilterOnChange : this.salesOverviewFilterOnChange;

    gridSchema?.forEach((gridElm: any) => {
      if (!gridElm?.inputs) {
        return;
      }

      if (gridElm.type === SECTION_TYPE.GlobalFilter) {
        return;
      }

      const componentName = gridElm.component?.name ?? '';
      const isProgressBarComponent = componentName.includes('DynamicProgressBarComponent');
      const isMapComponent = componentName.includes('DsMapDrilldownComponent');

      if (isMapComponent && isDealerOverview) {
        gridElm.inputs.globalPayloadSubject = this.dealerOverviewPayload;
      }
      if (isProgressBarComponent) {
        gridElm.inputs.globalPayloadSubject = isDealerOverview ? this.dealerOverviewPayload : this.salesOverviewPayload;
      }
      if (gridElm.inputs.apiConfig && !isProgressBarComponent && !isMapComponent) {
        gridElm.inputs.filterSchema ??= {};
        gridElm.inputs.filterSchema.globalPayloadSubject = isDealerOverview ? this.dealerOverviewPayload : this.salesOverviewPayload;
      }
    });
  }

  private emitAreaYearPayloadForTab(tab: TabConfig): void {
    const gridSchema = tab.inputs?.['gridSchema'] as any[] | undefined;
    const formName = gridSchema?.find((gridElm: any) => gridElm?.type === SECTION_TYPE.GlobalFilter)?.inputs?.dynamicFormArray?.formName;
    if (formName === 'dealerScorecardGlobalFilter') {
      this.emitDealerOverviewPayload();
    } else if (formName === 'salesOverviewGlobalFilter') {
      this.emitSalesOverviewPayload();
    }
  }

  private readonly dealerOverviewFilterOnChange = (event: FieldSelectedValue): void => {
    event?.FieldArray?.forEach((field) => {
      if (field.fieldName === event.FieldName) {
        field.selectedValue = event.Value ?? '';
      }
    });

    this.emitDealerOverviewPayload();
  };

  private emitDealerOverviewPayload(): void {
    this.dealerOverviewPayload.next({
      FieldName: DealerOverviewFilterTrigger,
      Value: {
        body: this.buildAreaYearPayload('dealerScorecardGlobalFilter'),
        title: {},
      },
    });
  }

  private readonly salesOverviewFilterOnChange = (event: FieldSelectedValue): void => {
    event?.FieldArray?.forEach((field) => {
      if (field.fieldName === event.FieldName) {
        field.selectedValue = event.Value ?? '';
      }
    });

    this.emitSalesOverviewPayload();
  };

  private emitSalesOverviewPayload(): void {
    this.salesOverviewPayload.next({
      FieldName: SalesOverviewFilterTrigger,
      Value: {
        body: this.buildAreaYearPayload('salesOverviewGlobalFilter'),
        title: {},
      },
    });
  }

  private buildAreaYearPayload(formName: string): Record<string, string> {
    const fields = this.getAreaYearFilterForm(formName)?.FieldsArray ?? [];
    const area = fields.find((field: any) => field?.fieldName === FieldNames.Area)?.selectedValue ?? '';
    const yearField = fields.find((field: any) => field?.fieldName === FieldNames.CalendarYear);
    const payload: Record<string, string> = { area: this.normalizeSalesOverviewArea(area) };
    if (yearField) {
      payload['year'] = `${yearField.selectedValue ?? ''}`.trim();
    }
    return payload;
  }

  private getAreaYearFilterForm(formName: string): any {
    for (const tab of this.tabConfig) {
      const gridSchema = tab.inputs?.['gridSchema'] as any[] | undefined;
      const filterSection = gridSchema?.find((gridElm: any) => gridElm?.inputs?.dynamicFormArray?.formName === formName);
      if (filterSection?.inputs?.dynamicFormArray) {
        return filterSection.inputs.dynamicFormArray;
      }
    }

    return null;
  }

  private normalizeSalesOverviewArea(area: any): string {
    const value = `${Array.isArray(area) ? area[0] : area ?? ''}`.trim();
    const areaMap: Record<string, string> = {
      '31': this.westernArea,
      '32': this.centralArea,
      '33': this.easternArea,
      '34': this.southernArea,
      west: this.westernArea,
      western: this.westernArea,
      'western area': this.westernArea,
      central: this.centralArea,
      'central area': this.centralArea,
      east: this.easternArea,
      eastern: this.easternArea,
      'eastern area': this.easternArea,
      south: this.southernArea,
      southern: this.southernArea,
      'southern area': this.southernArea,
    };

    return areaMap[value.toLowerCase()] ?? value;
  }

  private setupScorecardFilterActions(): void {
    const filterForm = this.getFilterForm();
    if (!filterForm) {
      return;
    }

    filterForm.onControlValueChanged = (event: any) => {
      const changed = filterForm.FieldsArray?.find((f: any) => f.fieldName === event?.FieldName);
      if (changed) {
        changed.selectedValue = event?.Value ?? '';
      }
      this.updateScorecardButtonsState(filterForm);
      if (event?.FieldName === 'dealerId' || event?.FieldName === FieldNames.CalendarYear) {
        this.emitDealerPerformanceDashboardPayload();
      }
    };

    filterForm.onButtonClicked = (event: { name: string }) => {
      if (event?.name === BUTTON_NAMES.Reset) {
        setTimeout(() => {
          (filterForm.FieldsArray ?? []).forEach((field: any) => {
            field.selectedValue = field.defaultValue ?? '';
          });
          this.updateScorecardButtonsState(filterForm);
          this.emitDealerPerformanceDashboardPayload();
        });
      }
    };

    this.updateScorecardButtonsState(filterForm);
  }

  private bindDealerPerformanceDashboardPayload(): void {
    this.getGridSchema().forEach((gridElm: any) => {
      if (!gridElm?.inputs || gridElm.inputs.elementId === DealerScorecardSectionNames.DashboardDealerSelector) {
        return;
      }
      const componentName = gridElm.component?.name ?? '';
      if (componentName.includes('DsMapDrilldownComponent')) {
        return;
      }
      if (componentName.includes('DynamicProgressBarComponent')) {
        gridElm.inputs.globalPayloadSubject = this.dealerPerformanceDashboardPayload;
        return;
      }
      gridElm.inputs.filterSchema ??= {};
      gridElm.inputs.filterSchema.globalPayloadSubject = this.dealerPerformanceDashboardPayload;
    });
  }

  private emitDealerPerformanceDashboardPayload(): void {
    const payload = this.buildDealerPerformanceDashboardPayload();
    this.dealerPerformanceDashboardPayload.next({
      FieldName: DealerPerformanceDashboardFilterTrigger,
      Value: {
        body: payload,
        title: {},
      },
    });
  }

  private updateScorecardButtonsState(filterForm: any): void {
    const fields = filterForm?.FieldsArray ?? [];
    const hasSelection = fields.some((f: any) => {
      const v = f?.selectedValue;
      if (Array.isArray(v)) {
        return v.length > 0;
      }
      return v !== null && v !== undefined && `${v}`.trim() !== '';
    });

    (filterForm?.ButtonsArray ?? []).forEach((btn: any) => {
      const name = `${btn?.name ?? ''}`.toLowerCase();
      if (name === BUTTON_NAMES.Search.toLowerCase() || name === BUTTON_NAMES.Reset.toLowerCase()) {
        btn.isDisabled = !hasSelection;
        btn.detectChanges?.();
      }
    });
  }

  private getGridSchema(): any[] {
    const scorecardTab = this.tabConfig.find((tab) => tab.tabId === 'dealerPerformanceDashboard');
    return (scorecardTab?.inputs?.['gridSchema'] as any[]) ?? [];
  }

  private getFilterForm(): any {
    const gridSchema = this.getGridSchema();
    return gridSchema?.[0]?.inputs?.['dynamicFormArray'];
  }

  private loadDealerDropdownOptions(): Promise<void> {
    const filterForm = this.getFilterForm();
    const filterFields = filterForm?.['FieldsArray'] as any[] | undefined;
    if (!filterFields?.length) {
      return Promise.resolve();
    }

    const dealerField = filterFields.find((field) => field?.fieldName === 'dealerId');
    if (!dealerField) {
      return Promise.resolve();
    }

    dealerField.isLoading = true;

    return this.service.getDataFromDataSource(GoogleAnalyticsDealerOptionsSearchConfig, ApiEnvNames['JobEnrollment']).then((response) => {
      const rows = this.getDealerRows(response?.result ?? []);
      const dealerOptions = Array.isArray(rows)
        ? rows.map((row: any) => {
          const dealerCode = this.readField(row, ['DEALER_CODE', 'dealerCode', 'Dlr Code', 'DlrCode', 'organisation', 'code']);
          const dealerName = this.readField(row, ['DEALER_NAME', 'dealerName', 'Dealer', 'name', 'organisationName', 'label']);
          return {
            fieldLabel: dealerName || dealerCode,
            fieldValue: dealerCode || dealerName,
          };
        })
          .filter((opt: any) => !!opt.fieldLabel && !!opt.fieldValue)
        : [];

      dealerField.options = dealerOptions;
      dealerField.isLoading = false;
    }).catch(() => {
      dealerField.options = [];
      dealerField.isLoading = false;
      this.commonUtils.showMatSnackBar('Unable to load dealer dropdown options');
    });
  }

  private buildDealerPerformanceDashboardPayload(): Record<string, string> {
    const fields = this.getFilterForm()?.FieldsArray ?? [];
    const dealerCode = `${fields.find((field: any) => field?.fieldName === 'dealerId')?.selectedValue ?? ''}`.trim();
    const year = `${fields.find((field: any) => field?.fieldName === FieldNames.CalendarYear)?.selectedValue ?? ''}`.trim();
    return { year, dealerCode };
  }

  private getDealerRows(result: any): any[] {
    if (!result) {
      return [];
    }

    if (!Array.isArray(result) && typeof result === 'object') {
      const dealerRows = result[FieldNames.DealerNumber]
        ?? result['dealerNumber']
        ?? result['dealers']
        ?? result['dealerList'];
      if (Array.isArray(dealerRows)) {
        return dealerRows;
      }
      for (const [key, value] of Object.entries(result)) {
        if (Array.isArray(value) && key.toLowerCase().includes('dealer')) {
          return value;
        }
      }
      return [];
    }

    if (!result.length) {
      return [];
    }

    const firstRow = result[0];
    if (Array.isArray(firstRow)) {
      return firstRow;
    }

    if (firstRow && typeof firstRow === 'object') {
      const dealerRows = firstRow[FieldNames.DealerNumber]
        ?? firstRow['dealerNumber']
        ?? firstRow['dealers']
        ?? firstRow['dealerList'];
      if (Array.isArray(dealerRows)) {
        return dealerRows;
      }
      for (const value of Object.values(firstRow)) {
        if (Array.isArray(value)) {
          return value;
        }
      }
    }

    return result;
  }

  private readField(row: any, keys: string[]): string {
    for (const key of keys) {
      const value = row?.[key];
      if (value !== undefined && value !== null && `${value}`.trim() !== '') {
        return `${value}`;
      }
    }
    return '';
  }

  private loadRegionDropdownOptions(): void {
    const filterForm = this.getFilterForm();
    const filterFields = filterForm?.['FieldsArray'] as any[] | undefined;
    if (!filterFields?.length) {
      return;
    }

    const regionField = filterFields.find((field) => field?.fieldName === 'region');
    if (!regionField) {
      return;
    }

    regionField.isLoading = true;

    this.service.getDataFromDataSource( { method: FETCH_METHODS['Get'], path: 'aseDropdownOptions' },ApiEnvNames['AseReport']).then((response) => {
      if (response?.status !== API_STATUS['Success'] || !Array.isArray(response?.result)) {
        throw new Error('Region dropdown API failed');
      }

      const rows = this.getDealerRows(response.result);
      const regionOptions = rows
        .map((row: any) => {
          const regionLabel = this.readField(row, ['regionName']);
          const regionValue = this.readField(row, ['regionCode']);
          return {
            fieldLabel: regionLabel || regionValue,
            fieldValue: regionValue || regionLabel,
          };
        })
        .filter((opt: any) => !!opt.fieldLabel && !!opt.fieldValue);

      regionField.options = regionOptions;
      regionField.isLoading = false;
    }).catch(() => {
      regionField.options = [];
      regionField.isLoading = false;
      this.commonUtils.showMatSnackBar('Unable to load region dropdown options');
    });
  }

  private loadSalesOverviewEventOptions(): Promise<void> {
    const eventField = SalesOverviewFilterSchema.FieldsArray?.find((field: any) => field?.fieldName === 'event_name');
    if (!eventField) {
      return Promise.resolve();
    }

    eventField.isLoading = true;

    return this.service.getDataFromDataSource(fetchParamsEventListForDashboard, ApiEnvNames['EventDashboard']).then((response) => {
      if (response?.status !== API_STATUS['Success'] || !Array.isArray(response?.result)) {
        throw new Error('Event dropdown API failed');
      }

      const eventOptions = response.result
        .map((row: any) => {
          const eventName = this.readField(row, ['event_name', 'eventName']);
          return { fieldLabel: eventName, fieldValue: eventName };
        })
        .filter((opt: any) => !!opt.fieldLabel);

      eventField.options = eventOptions;
      if (eventOptions.length) {
        eventField.defaultValue = eventOptions[0].fieldValue;
        eventField.selectedValue = eventOptions[0].fieldValue;
      }
      eventField.isLoading = false;
    }).catch(() => {
      eventField.options = [];
      eventField.isLoading = false;
      this.commonUtils.showMatSnackBar('Unable to load event dropdown options');
    });
  }
}
